<template>
  <BaseModal
    :open="!!content"
    size="auto"
    borderless
    center-in-window
    @close="close"
  >
    <template v-if="shown.content" #default>
      <div class="flex w-[min(30rem,calc(100vw-3rem))] flex-col gap-6">
        <div class="flex flex-col gap-2">
          <DialogTitle
            as="h2"
            class="font-artex text-2xl leading-tight font-bold text-burgundy-900"
          >
            {{ shown.content.title
            }}<span v-if="shown.content.celebrate" aria-hidden="true"> 🎉</span>
          </DialogTitle>
          <p class="text-sm/6 text-gray-600">{{ shown.content.body }}</p>
        </div>
        <div class="flex flex-col gap-3">
          <ol
            aria-label="User levels"
            class="flex gap-1 rounded-xl bg-gray-50 p-1"
          >
            <template v-for="(step, index) in steps" :key="step.level">
              <!-- Hidden, so the list still counts four levels. -->
              <li
                v-if="index > 0"
                aria-hidden="true"
                class="flex flex-none items-center"
              >
                <Icon
                  name="angles-right"
                  class="size-4"
                  :class="step.ahead ? 'text-gray-300' : 'text-purple-400'"
                />
              </li>
              <li
                :aria-current="step.current ? 'step' : undefined"
                class="flex min-w-0 flex-1 flex-col items-center gap-2 rounded-lg px-0.5 pt-3.5 pb-2.5"
                :class="
                  step.current
                    ? 'bg-white shadow-sm ring-1 ring-purple-200'
                    : ''
                "
              >
                <LevelBadge
                  :level="step.level"
                  size="md"
                  :show-label="false"
                  aria-hidden="true"
                  :class="{ 'opacity-40 grayscale': step.ahead }"
                />
                <span
                  class="text-[11px] font-semibold sm:text-xs"
                  :class="
                    step.current
                      ? 'text-burgundy-900'
                      : step.ahead
                        ? 'text-gray-500'
                        : 'text-gray-700'
                  "
                >
                  {{ LEVEL_COPY[step.level].name }}
                  <span v-if="step.current" class="sr-only">
                    (your user level)
                  </span>
                </span>
              </li>
            </template>
          </ol>
          <p class="text-sm text-gray-700">
            <span
              v-if="shown.content.level !== 'lightkeeper'"
              class="font-semibold text-gray-900"
              >Next:
            </span>
            {{ shown.content.next }}
          </p>
        </div>
        <p
          v-if="shown.nextItem"
          class="border-t border-gray-100 pt-4 text-sm text-gray-600"
        >
          A good place to start:
          <span class="font-semibold text-gray-900">{{
            shown.nextItem.title
          }}</span>
        </p>
      </div>
    </template>
    <template #footer>
      <button
        v-if="shown.tourKey || shown.nextItem"
        type="button"
        class="btn"
        @click="close"
      >
        Later
      </button>
      <button
        v-if="shown.tourKey"
        type="button"
        class="btn-primary"
        @click="emit('start', shown.tourKey)"
      >
        Take the tour
      </button>
      <button
        v-else-if="shown.nextItem"
        type="button"
        class="btn-primary"
        @click="startNext"
      >
        Show me
      </button>
      <button v-else type="button" class="btn-primary" @click="close">
        Continue
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, PropType, shallowRef, watch } from "vue";
import { DialogTitle } from "@headlessui/vue";
import Icon from "@/components/Core/Icon.vue";
import BaseModal from "@/components/Modal/BaseModal.vue";
import LevelBadge from "@/components/SetupProgress/LevelBadge.vue";
import { LEVEL_COPY } from "@/copy/setupProgress";
import { type SetupLevel, SETUP_LEVELS } from "@/services/setup_progress";
import { launchConfetti } from "@/utils/confetti";

export interface LevelModalContent {
  level: SetupLevel;
  title: string;
  body: string;
  next: string;
  celebrate: boolean;
}

const props = defineProps({
  content: {
    type: Object as PropType<LevelModalContent | null>,
    default: null,
  },
  nextItem: {
    type: Object as PropType<{ key: string; title: string } | null>,
    default: null,
  },
  tourKey: { type: String as PropType<string | null>, default: null },
});

const emit = defineEmits({
  close: () => true,
  start: (_key: string) => true,
});

// Kept while the modal fades out after the content is cleared, so the closing
// dialog doesn't go blank or swap its buttons.
const shown = shallowRef<{
  content: LevelModalContent | null;
  nextItem: { key: string; title: string } | null;
  tourKey: string | null;
}>({ content: null, nextItem: null, tourKey: null });
watch(
  () => [props.content, props.nextItem, props.tourKey] as const,
  ([content, nextItem, tourKey]) => {
    if (content) shown.value = { content, nextItem, tourKey };
  },
  { immediate: true },
);

const steps = computed(() => {
  const reached = SETUP_LEVELS.indexOf(
    shown.value.content?.level ?? "explorer",
  );
  return SETUP_LEVELS.map((level, index) => ({
    level,
    current: index === reached,
    ahead: index > reached,
  }));
});

// After the fade-in, so it lands where the user is looking.
const CONFETTI_DELAY_MS = 300;

// Keyed on the title: a refetch rebuilds the content, but it's the same news.
watch(
  () => (props.content?.celebrate ? props.content.title : null),
  (celebrating, _previous, onCleanup) => {
    if (!celebrating) return;
    const timer = setTimeout(launchConfetti, CONFETTI_DELAY_MS);
    onCleanup(() => clearTimeout(timer));
  },
  { immediate: true },
);

function close() {
  emit("close");
}

function startNext() {
  if (shown.value.nextItem) emit("start", shown.value.nextItem.key);
}
</script>
